import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CountrySelect,
  LogSentryException,
  Modal,
  NumberInput,
  SegmentedControl,
  ServiceError,
  Spinner,
  useRemoteConfig,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useBlocker, useLocation, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { ReactComponent as Icon_ApplePay24 } from "../../assets/images/ApplePay24.svg";
import { ReactComponent as Icon_CreditCardFilled24 } from "../../assets/images/CreditCardFilled24.svg";
import { chargebeeTrialDays, type InviteStorageData, sessionStorageKeys } from "../../config";
import useNavigation from "../../hooks/navigation";
import { TeamsUserRoles } from "../../hooks/useAccountTypes";
import {
  EmptyCheckoutEstimateFormData,
  type EstimateResponseData,
  type StartCheckoutServiceErrorResponse,
  useCreateCheckout,
  useSubmitCheckout,
} from "../../hooks/useCheckout";
import { useEventListener } from "../../hooks/useEventListener";
import { useTracker } from "../../hooks/useTracker";
import {
  APPLE_PAY_NOT_SUPPORTED,
  ApplePayButtonId,
  PaymentType,
  useChargebee,
} from "../../lib/ChargebeeAdapter";
import { ElementClickAction, ModalShownAction } from "../../lib/TrackingAdapter";
import { SearchParam, ServiceErrorCode, SubscriptionPeriod, SubscriptionType } from "../../types";
import ChargebeeTrialTimeline from "../ChargebeeTrialTimeline/ChargebeeTrialTimeline";
import CreditCardInput from "../CreditCardInput/CreditCardInput";
import Heading from "../Heading/Heading";
import { Tiles } from "../Tiles/Tiles";
import { type CheckoutFormData, schema } from "./ChargebeeCheckoutSchema";
import BillingInfoForm from "./components/BillingInfoForm/BillingInfoForm";
import CheckoutFormProvider from "./components/CheckoutFormProvider";
import CheckoutSummary from "./components/CheckoutSummary/CheckoutSummary";
import { CheckoutErrorModal } from "./components/CreateCheckoutErrorModal/CreateCheckoutErrorModal";
import { PriceSummary } from "./components/PriceSummary/PriceSummary";

import style from "./ChargebeeCheckout.module.css";

export const useInitialPeriod = () => {
  const location = useLocation();

  return useMemo(
    () =>
      new URLSearchParams(location.search).get("period") === "monthly"
        ? SubscriptionPeriod.Monthly
        : SubscriptionPeriod.Yearly,
    [location.search]
  );
};

export const useWithInvite = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Clear invite emails if the user navigates away from the checkout page
  useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname !== nextLocation.pathname) {
      sessionStorage.removeItem(sessionStorageKeys.inviteData);
    }
    return false;
  });

  useEffect(() => {
    const withInvite = searchParams.has(SearchParam.WithInvite);
    const inviteData = sessionStorage.getItem(sessionStorageKeys.inviteData);
    if (!withInvite || !inviteData) {
      sessionStorage.removeItem(sessionStorageKeys.inviteData);
      searchParams.delete(SearchParam.WithInvite);
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return searchParams.has(SearchParam.WithInvite);
};

const ChargebeeCheckout = () => {
  const withInvite = useWithInvite();
  const initialPeriod = useInitialPeriod();

  const [submitError, setSubmitError] = useState<string | null>(null);

  const navigate = useNavigation();
  const onCheckoutClose = useCallback(
    () => navigate.goToProfile([SearchParam.SubscriptionPeriod, SearchParam.SubscriptionTier]),
    [navigate]
  );
  const tracker = useTracker();
  const isCheckoutSuccess = useRef(false);
  useEventListener("beforeunload", tracker.trackCheckoutAbandonment.bind(tracker));
  useEffect(() => {
    return () => {
      if (!isCheckoutSuccess.current) {
        tracker.trackCheckoutAbandonment();
      }
    };
  }, [tracker]);

  const { isLoading: isChargebeeLoading, isApplePaySupported } = useChargebee();

  const applePayFeatureFlag = useRemoteConfig("web_apple_pay-ff");
  const isApplePayFeatureFlagLoading = applePayFeatureFlag === undefined;
  const isApplePayEnabled =
    isApplePaySupported && !isApplePayFeatureFlagLoading && applePayFeatureFlag;

  const {
    data: createCheckoutData,
    error: createCheckoutError,
    isPending: createCheckoutIsPending,
    isSuccess: createCheckoutIsSuccess,
    mutateAsync: createCheckoutMutation,
  } = useCreateCheckout();

  const [searchParams] = useSearchParams();
  const showPeriodPickerParam = searchParams.get(SearchParam.CheckoutShowPeriodPicker) === "true";

  const showPeriodPicker = useMemo(
    () =>
      !createCheckoutData?.fixedPeriod &&
      (showPeriodPickerParam || initialPeriod === SubscriptionPeriod.Monthly),
    [createCheckoutData?.fixedPeriod, showPeriodPickerParam, initialPeriod]
  );

  useEffect(() => {
    if (createCheckoutIsSuccess) {
      tracker.trackCheckoutFlowLoaded("chargebee");
    }
  }, [createCheckoutIsSuccess, tracker]);

  const fromAddSeats = searchParams.has(SearchParam.FromAddSeats);
  const couponCodeFromURL = searchParams.get(SearchParam.SubscriptionCouponCode) ?? undefined;
  const isMigrationConsentRequired =
    createCheckoutData?.migratingFromType === SubscriptionType.AppStore &&
    createCheckoutData?.migrationAutoRenewEnabled;

  const inviteData = useMemo((): InviteStorageData | null => {
    const stored = sessionStorage.getItem(sessionStorageKeys.inviteData);
    return stored ? JSON.parse(stored) : null;
  }, []);

  const isContributorInvite = useMemo(() => {
    return inviteData?.role === TeamsUserRoles.Contributor;
  }, [inviteData]);

  const methods = useForm<CheckoutFormData, unknown, CheckoutFormData>({
    resolver: zodResolver(
      schema.superRefine((val, ctx) => {
        if (isMigrationConsentRequired && !val.migrationConsent) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["migrationConsent"],
          });
        }
      })
    ),
    mode: "onSubmit",
    defaultValues: async () => {
      const inviteEmails = inviteData?.emails ?? [];
      const inviteRole = inviteData?.role;
      const minSeats = fromAddSeats ? 2 : 1;
      let formRole = undefined;
      if (inviteRole === TeamsUserRoles.Editor || inviteRole === TeamsUserRoles.Contributor) {
        formRole = inviteRole;
      }

      const editorSeatCount = isContributorInvite ? minSeats : inviteEmails.length + minSeats;

      try {
        const { billingDetails, fixedPeriod } = await createCheckoutMutation({
          period: initialPeriod,
        });
        return {
          couponCode: couponCodeFromURL,
          ...EmptyCheckoutEstimateFormData,
          ...billingDetails,
          paymentType: PaymentType.Card,
          period: fixedPeriod ?? initialPeriod,
          inviteEmails,
          role: formRole,
          editorSeatCount,
          migrationConsent: false,
        };
      } catch {
        return {
          couponCode: couponCodeFromURL,
          ...EmptyCheckoutEstimateFormData,
          paymentType: PaymentType.Card,
          period: initialPeriod,
          inviteEmails,
          role: formRole,
          editorSeatCount,
          migrationConsent: false,
        };
      }
    },
  });

  const {
    control,
    formState: { errors, isSubmitting },
    register,
    setError,
    setValue,
    reset: resetForm,
  } = methods;

  const {
    mutateAsync: submitCheckoutMutation,
    isPending: submitCheckoutIsPending,
    reset: resetSubmitCheckoutMutation,
  } = useSubmitCheckout();

  const countryCode = methods.watch("countryCode");
  const paymentType = methods.watch("paymentType");

  useEffect(() => {
    // TODO: send the specific invalid fields to telemetry
    if (errors && Object.keys(errors).length > 0) {
      tracker.trackCheckoutFlowIssue(
        Boolean(createCheckoutData?.trialEnd),
        "invalid_billing_fields"
      );
    }
  }, [errors, tracker, createCheckoutData]);

  const isLoading =
    createCheckoutIsPending ||
    !createCheckoutData ||
    submitCheckoutIsPending ||
    isApplePayFeatureFlagLoading;

  const submit = useCallback(
    async (
      estimateResponseData: EstimateResponseData | undefined,
      checkoutID: string | undefined,
      formData: CheckoutFormData
    ) => {
      try {
        const isTrial = Boolean(estimateResponseData?.estimate.trialEnd);
        if (estimateResponseData && checkoutID) {
          await submitCheckoutMutation(
            {
              checkoutID,
              estimateResponseData,
              formData,
            },
            {
              onSuccess: () => {
                const additionalQueryParams: { [key in SearchParam]?: string } = {};
                isCheckoutSuccess.current = true;
                tracker.trackCheckoutFlowCompleted(
                  isTrial,
                  "chargebee",
                  formData.editorSeatCount,
                  paymentType
                );

                if (isTrial) {
                  toast.success("Your team\u2019s trial is activated!");
                } else {
                  toast.success("Your team\u2019s Shapr3D Pro subscription is activated!");
                }

                if (withInvite) {
                  toast.success("Invitation sent!");
                  additionalQueryParams[SearchParam.WithInvite] = withInvite.toString();
                } else if (formData.editorSeatCount) {
                  toast.success(
                    `${formData.editorSeatCount} ${
                      formData.editorSeatCount > 1 ? "seats" : "seat"
                    } added to your team.`
                  );
                }

                navigate.goToPaymentSuccess({
                  additionalQueryParams,
                  omitQueryParams: [
                    SearchParam.CardError,
                    SearchParam.SubscriptionPeriod,
                    SearchParam.SubscriptionTier,
                  ],
                });
              },
              onError: (error) => {
                if (error.message === "CVC Declined" || error.message.includes("incorrect_cvc")) {
                  setError("card", { message: "Invalid CVV number", type: "validate" });
                }
                if (error.message === "Year is invalid") {
                  setError("card", { message: "Invalid expiration date", type: "validate" });
                }

                if (
                  error.name === APPLE_PAY_NOT_SUPPORTED ||
                  error.message === APPLE_PAY_NOT_SUPPORTED
                ) {
                  tracker.trackApplePaySetupFailed({ location: "checkout" });
                  LogSentryException({
                    error: new Error("Apple Pay is not supported"),
                    level: "error",
                  });
                  setError("paymentType", {
                    message:
                      "Something went wrong while setting up Apple Pay. Please try again or choose a different payment method.",
                    type: "validate",
                  });
                  return;
                }

                if (
                  error instanceof ServiceError &&
                  error.hasCode(ServiceErrorCode.ValidationError)
                ) {
                  const err: ServiceError = error;
                  err.assertType<StartCheckoutServiceErrorResponse>();
                  tracker.trackCheckoutFlowIssue(isTrial, "invalid_billing_fields");

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.zip) {
                    setError("zip", { message: "Invalid post code", type: "validate" });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.countryCode) {
                    LogSentryException({
                      error: new Error("Invalid country during checkout"),
                      level: "error",
                    });
                    setError("countryCode", { message: "Invalid country", type: "validate" });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.stateCode) {
                    LogSentryException({
                      error: new Error("Invalid state during checkout"),
                      level: "error",
                    });
                    setError("stateCode", { message: "Invalid state", type: "validate" });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.city) {
                    setError("city", { message: "Invalid city", type: "validate" });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.line1) {
                    setError("line1", { message: "Invalid address", type: "validate" });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.line2) {
                    setError("line2", { message: "Invalid address", type: "validate" });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.vatNumber) {
                    setError("vatNumber", { message: "Invalid VAT number", type: "validate" });
                  }

                  return;
                }

                if (error instanceof Error) {
                  setSubmitError(error.message);
                  tracker.trackCheckoutFlowFailed(
                    isTrial,
                    "card_authorization_failed",
                    formData.editorSeatCount
                  );
                }
              },
            }
          );
        }
      } catch (error) {
        // This is to not throw Unhandled Promise Rejection error
      }
    },
    [submitCheckoutMutation, tracker, paymentType, withInvite, navigate, setError]
  );

  const onCreditCardValidationErrors = useCallback(() => {
    tracker.trackCheckoutFlowIssue(Boolean(createCheckoutData?.trialEnd), "card_validation_failed");
  }, [tracker, createCheckoutData]);

  const isApplePayModalOpen = paymentType === PaymentType.ApplePay && isSubmitting;
  const hasTrackedApplePayModalModalOpen = useRef(false);
  useEffect(() => {
    if (isApplePayModalOpen && !hasTrackedApplePayModalModalOpen.current) {
      hasTrackedApplePayModalModalOpen.current = true;
      tracker.trackModalShown(ModalShownAction.APPLE_PAY_CONFIRMATION);
    }

    return () => {
      hasTrackedApplePayModalModalOpen.current = false;
    };
  }, [isApplePayModalOpen, tracker]);

  return (
    <CheckoutFormProvider
      methods={methods}
      submitError={submitError}
      checkout={createCheckoutData}
      submit={submit}
      isMigrationConsentRequired={isMigrationConsentRequired}
    >
      <div className={style.wrapper}>
        {isLoading && (
          <div className={style.loader}>
            <Spinner></Spinner>
            {submitCheckoutIsPending && paymentType === PaymentType.Card && (
              <div className={style.loaderText}>Contacting your card issuer...</div>
            )}
          </div>
        )}
        <div className={classNames(style.pageWrapper, !isLoading && style.show)}>
          <div className={style.headerWrapper}>
            {createCheckoutData?.trialEnd ? (
              <>
                <Heading type="h2">Start your {chargebeeTrialDays}-day free trial</Heading>
                <ChargebeeTrialTimeline trialDays={chargebeeTrialDays} />
              </>
            ) : (
              <>
                <Heading type="h2">Subscribe now</Heading>
                <div className={style.subtitle}>Access to all features without limits.</div>
              </>
            )}
          </div>
          <div className={style.inlineCheckoutWrapper}>
            <div className={style.paymentDetails}>
              {!withInvite && (
                <div className={style.subgroup}>
                  <div>
                    <span className={style.checkoutLineText}>Number of seats </span>
                    <span>(Including you)</span>
                  </div>
                  <Controller
                    name="editorSeatCount"
                    render={({ field: { onChange, value } }) => {
                      const defaultSeats = fromAddSeats ? 2 : 1;
                      return (
                        <NumberInput
                          defaultValue={defaultSeats}
                          min={defaultSeats}
                          max={50}
                          onChange={(_, value) => {
                            tracker.trackButtonClick({
                              action: ElementClickAction.CHANGE_NUMBER_OF_SEATS,
                              details: `${value ?? defaultSeats}`,
                            });
                            onChange(value ?? defaultSeats);
                          }}
                          value={value ?? defaultSeats}
                        />
                      );
                    }}
                  />
                </div>
              )}
              {showPeriodPicker && (
                <div className={style.subgroup}>
                  <div className={style.checkoutLineText}>Billing period</div>
                  <Controller
                    name="period"
                    control={control}
                    render={({ field: { onChange } }) => (
                      <SegmentedControl
                        className={style.periodPicker}
                        defaultChecked={initialPeriod === SubscriptionPeriod.Monthly}
                        onChange={(e) => {
                          const period = e.target.checked
                            ? SubscriptionPeriod.Monthly
                            : SubscriptionPeriod.Yearly;
                          onChange(period);
                          tracker.trackButtonClick({
                            action: ElementClickAction.CHANGE_BILLING_PERIOD,
                            details: period,
                          });
                        }}
                        optionA={`Yearly`}
                        optionASecondaryText={`- Save 34%`}
                        optionB={`Monthly`}
                      />
                    )}
                  />
                </div>
              )}

              {isApplePayEnabled && (
                <div className={style.subgroup}>
                  <div className={style.checkoutLineText}>Payment method</div>
                  <Tiles
                    registerAttrs={register("paymentType")}
                    options={[
                      {
                        value: PaymentType.ApplePay,
                        label: "Apple Pay",
                        icon: <Icon_ApplePay24 />,
                      },
                      {
                        value: PaymentType.Card,
                        label: "Credit/debit card",
                        icon: <Icon_CreditCardFilled24 />,
                      },
                    ]}
                  />
                  {errors?.paymentType && (
                    <div className={style.errorMessage}>{errors.paymentType.message}</div>
                  )}
                </div>
              )}

              {!isChargebeeLoading && (
                <div
                  className={classNames(
                    style.subgroup,
                    paymentType !== PaymentType.Card && style.hidden
                  )}
                >
                  <div className={style.checkoutLineText}>Credit card details</div>
                  <CreditCardInput
                    name="card"
                    control={control}
                    onValidationErrors={onCreditCardValidationErrors}
                  />
                  <div className={style.creditCardLocationContainer}>
                    <CountrySelect
                      errorMessage={errors.countryCode?.message}
                      required
                      value={countryCode}
                      onChange={(e) => {
                        setValue("countryCode", e.target.value);
                      }}
                    />
                  </div>
                </div>
              )}

              <BillingInfoForm />
            </div>
            <CheckoutSummary />
          </div>
        </div>
        <Modal
          title="Apple Pay"
          open={isApplePayModalOpen}
          onClose={() => {
            resetSubmitCheckoutMutation();
            resetForm(undefined, { keepDirtyValues: true });
          }}
        >
          <div className={style.applePayCheckoutSummaryContainer}>
            <PriceSummary />
          </div>
          <div className={style.applePayButtonContainer}>
            <Button className={style.applePayButtonPlaceholder} loading />
            <div
              id={ApplePayButtonId}
              onClick={() => {
                tracker.trackApplePayButtonClicked({ location: "checkout" });
              }}
            />
          </div>
        </Modal>
        <CheckoutErrorModal
          onClose={onCheckoutClose}
          error={createCheckoutError}
          open={Boolean(createCheckoutError)}
        />
      </div>
    </CheckoutFormProvider>
  );
};

export default ChargebeeCheckout;
